// src/pages/orderAssignment/components/OrderDetailsModal.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  X,
  User,
  MapPin,
  Phone,
  Calendar,
  Clock,
  Package,
  ChevronDown,
  Phone as PhoneIcon,
  Eye,
  AlertCircle,
  Edit2,
  Save,
  XCircle as CancelIcon,
  Edit,
  ExternalLink,
  DollarSign,
  Search,
  Minus,
  Plus,
  Hash,
  Truck,
  Receipt,
  ShoppingCart,
  Store,
  Loader2,
  Star,
  Plus as PlusIcon,
  Percent,
  Users,
  CheckCircle,
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../hooks/usePermissions';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { formatCurrency, calculateLineTotal, calculateOrderTotals, getCrossSellMaxQty } from '../../../utils/orderCalculations';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import { getEcomOrder, getEcomOrders, updateEcomOrder } from '../../../services/api/orders';
import { getDeliveryCharge } from '../../../services/api/deliveryCharges';
import useProductSearch from '../../../hooks/useProductSearch';
import PhoneNumberInput from '../../../components/ui/PhoneNumberInput/PhoneNumberInput';
import DiscountPercentagePopup from '../../../components/ui/DiscountPercentagePopup';
import BundleBadge from '../../../components/ui/BundleBadge/BundleBadge';
import BundleItemCard from '../../../components/orders/BundleItemCard/BundleItemCard';
import BundleItemGroup from '../../../components/orders/BundleItemGroup/BundleItemGroup';
import {
  groupOrderItems, getItemMergeKey, explodeBundle, rescaleBundleRows,
  applyBundleCrossSell, getBundleCrossSellMax, getBundleCrossSellCount,
} from '../../../utils/orderItemGrouping';
import styles from './OrderDetailsModal.module.css';

const CITY_UNKNOWN_ID = 9999;

// ─── Inline city search hook ──────────────────────────────────────────────────
const useCitySearchInline = (cities = []) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);

  const fuzzyMatch = useCallback((text, query) => {
    if (!query) return { score: 0, matches: false };
    const tL = text.toLowerCase(), qL = query.toLowerCase();
    if (tL === qL)            return { score: 300, matches: true };
    if (tL.startsWith(qL))   return { score: 200, matches: true };
    if (tL.includes(qL))     return { score: 100, matches: true };
    let ti = 0, qi = 0, m = 0;
    while (ti < tL.length && qi < qL.length) { if (tL[ti] === qL[qi]) { m++; qi++; } ti++; }
    if (qi === qL.length) return { score: (m / qL.length) * 50, matches: true };
    return { score: 0, matches: false };
  }, []);

  const filteredCities = useMemo(() => {
    if (!searchTerm.trim()) return [];
    return cities
      .map(c => { const r = fuzzyMatch(c.name || '', searchTerm); return { ...c, score: r.score, hasMatch: r.matches }; })
      .filter(c => c.hasMatch).sort((a, b) => b.score - a.score).slice(0, 10);
  }, [cities, searchTerm, fuzzyMatch]);

  const handleSearchChange = useCallback((v) => { setSearchTerm(v); setSelectedIndex(-1); setShowResults(!!v.trim()); }, []);
  const handleKeyDown = useCallback((e, onSelect) => {
    if (!showResults || !filteredCities.length) return;
    if (e.key === 'ArrowDown' || e.key === 'Tab') { e.preventDefault(); setSelectedIndex(p => p < filteredCities.length - 1 ? p + 1 : 0); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => p > 0 ? p - 1 : filteredCities.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); if (selectedIndex >= 0 && selectedIndex < filteredCities.length) onSelect(filteredCities[selectedIndex]); }
    else if (e.key === 'Escape') { setShowResults(false); setSelectedIndex(-1); }
  }, [showResults, filteredCities, selectedIndex]);
  const hideResults = useCallback(() => { setShowResults(false); setSelectedIndex(-1); }, []);
  const showResultsDropdown = useCallback(() => { if (searchTerm.trim()) setShowResults(true); }, [searchTerm]);
  const initSearch = useCallback((name) => { setSearchTerm(name || ''); setShowResults(false); setSelectedIndex(-1); }, []);

  return { searchTerm, filteredCities, selectedIndex, showResults, handleSearchChange, handleKeyDown, hideResults, showResultsDropdown, initSearch };
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── Delivery charge helpers ──────────────────────────────────────────────────
const computeDeliveryWithWeight = (base, extraPerKg, weight) => {
  const b = parseFloat(base) || 0;
  const e = parseFloat(extraPerKg) || 0;
  const w = parseFloat(weight) || 1;
  return (w > 1 && e > 0) ? b + (Math.ceil(w) - 1) * e : b;
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── Offer resolution helpers ─────────────────────────────────────────────────
const resolveOffer = (offers, quantity) => {
  if (!offers || !offers.length) return null;
  const qty = parseInt(quantity) || 1;
  const equalMatch = offers.find(o => o.criteria === 'equal' && parseInt(o.quantity) === qty);
  if (equalMatch) return equalMatch;
  const gteMatches = offers
    .filter(o => o.criteria === 'gte' && qty >= parseInt(o.quantity))
    .sort((a, b) => parseInt(b.quantity) - parseInt(a.quantity));
  return gteMatches[0] || null;
};
// When an offer is flagged applyAsDiscount, the unit price stays at the base price —
// the saving is expressed via resolveOfferLineDiscount() instead.
const resolveOfferPrice = (offers, qty, fallback) => {
  const o = resolveOffer(offers, qty);
  if (!o) return fallback;
  return o.applyAsDiscount ? fallback : parseFloat(o.unitPrice);
};
const resolveOfferLineDiscount = (offers, qty, basePrice) => {
  const o = resolveOffer(offers, qty);
  if (!o || !o.applyAsDiscount) return 0;
  const q = parseInt(qty) || 1;
  return Math.max(0, (parseFloat(basePrice) - parseFloat(o.unitPrice)) * q);
};
const resolveOfferDelivery = (offers, qty) => {
  const o = resolveOffer(offers, qty);
  return (o && o.overrideDeliveryCharge != null) ? parseFloat(o.overrideDeliveryCharge) : null;
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── Items edit helpers ───────────────────────────────────────────────────────
const itemsToEditState = (orderItems) =>
  (orderItems || []).map(item => ({
    id: item.id,
    productId: item.productId,
    variationId: item.variationId || null,
    displayName: item.Product
      ? item.Product.name + (item.ProductVariation ? ` - ${item.ProductVariation.attributeDisplay}` : '')
      : item.displayName || '',
    sku: item.ProductVariation?.sku || item.Product?.sku || item.sku || '',
    quantity: item.quantity,
    unitPrice: parseFloat(item.unitPrice),
    lineDiscount: parseFloat(item.lineDiscount || 0),
    manuallyDiscounted: false,
    // keep full product ref for stock display
    Product: item.Product,
    ProductVariation: item.ProductVariation,
    // temp flag to distinguish newly added vs original
    _isNew: false,
    offers: item.offers || [],
    // offerDeliveryOverride intentionally omitted — only set when user actively changes quantity
    isCrossSell: !!item.crossSellInfo,
    crossSellQuantity: item.crossSellInfo?.crossSellQuantity != null ? Number(item.crossSellInfo.crossSellQuantity) : null,
    crossSellById: item.crossSellInfo?.crossSellBy ?? null,
    baselineQuantity: item.crossSellInfo?.baselineQuantity != null ? Number(item.crossSellInfo.baselineQuantity) : null,
    originalQuantity: item.quantity,
    ...(item.bundleGroupKey && {
      bundleProductId: item.bundleProductId,
      bundleGroupKey: item.bundleGroupKey,
      bundleQuantity: parseFloat(item.bundleQuantity) || 1,
      bundleName: item.bundleProduct?.name || null,
      bundleSku: item.bundleProduct?.sku || null,
      componentQuantity: Number(item.quantity) / (parseFloat(item.bundleQuantity) || 1),
      componentLineDiscount: parseFloat(item.lineDiscount || 0) / (parseFloat(item.bundleQuantity) || 1),
    }),
  }));

const calcGrandTotal = (items, deliveryCharge) => {
  const totals = calculateOrderTotals(items, deliveryCharge);
  return totals.grandTotal;
};

const formatNum = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '' : n.toString();
};
// ─────────────────────────────────────────────────────────────────────────────

const OrderDetailsModal = ({
  order,
  availableStatuses,
  orderStatusReasons,
  cities = [],
  isResellerDropshipper = false,
  onClose,
  onStatusUpdate,
  onUpdate,
  assignedUsers = [],
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { settings: crossSellingSettings } = useBusinessSettings('crossSelling');
  const crossSellEnabled = !!crossSellingSettings.enabled;
  const currentUserId = user?.id;

  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [expandedStatusKey, setExpandedStatusKey] = useState(null);
  const [confirmingStatusKey, setConfirmingStatusKey] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [holdUntilDate, setHoldUntilDate] = useState('');
  const [showPreviousOrders, setShowPreviousOrders] = useState(false);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loadingPreviousOrders, setLoadingPreviousOrders] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [cancellingOrderIds, setCancellingOrderIds] = useState(new Set());
  const [cancelledOrderIds, setCancelledOrderIds] = useState(new Set());

  const [localOrder, setLocalOrder] = useState(order);
  const createdByUserId = localOrder.createdBy?.id ?? localOrder.createdByUserId ?? null;

  // Customer name editing
  const [isEditingCustomerName, setIsEditingCustomerName] = useState(false);
  const [customerNameValue, setCustomerNameValue] = useState(order.customerName || '');

  // Phone numbers editing
  const [isEditingPhoneNumbers, setIsEditingPhoneNumbers] = useState(false);
  const [editPhoneNumbers, setEditPhoneNumbers] = useState([]);
  const [phoneValidationErrors, setPhoneValidationErrors] = useState({});

  // Notes editing
  const [isEditingDeliveryNotes, setIsEditingDeliveryNotes] = useState(false);
  const [isEditingSpecialNotes, setIsEditingSpecialNotes] = useState(false);
  const [deliveryNotesValue, setDeliveryNotesValue] = useState(order.deliveryNotes || '');
  const [specialNotesValue, setSpecialNotesValue] = useState(order.specialNotes || '');

  // Address editing
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressValue, setAddressValue] = useState(order.address || '');
  const [selectedCityId, setSelectedCityId] = useState(order.cityId || null);
  const [selectedCityName, setSelectedCityName] = useState(order.City?.name || '');
  const [addressError, setAddressError] = useState('');
  const citySearch = useCitySearchInline(cities);
  const cityInputRef = useRef(null);

  // ─── Order items / delivery charge editing ────────────────────────────────
  const [isEditingItems, setIsEditingItems] = useState(false);
  const [editItems, setEditItems] = useState([]);
  // { itemId, anchorEl } — anchorEl is the % button DOM node used for portal positioning
  const [discountPopupState, setDiscountPopupState] = useState(null);
  const [editDeliveryCharge, setEditDeliveryCharge] = useState(0);
  // snapshot taken when edit mode opens — used for diff
  const originalItemsSnapshotRef = useRef(null);
  const originalDeliveryChargeSnapshotRef = useRef(null);
  const productSearchInputRef = useRef(null);
  // base delivery charge fetched when edit mode opens — used to restore after offer overrides clear
  const baseDeliveryChargeRef = useRef(null);
  const extraChargePerKgRef = useRef(null);

  const shopId = localOrder.Shop?.id || localOrder.shopId;

  const {
    searchTerm: productSearchTerm,
    searchResults,
    isSearching: isProductSearching,
    selectedIndex: productSelectedIndex,
    showResults: showProductResults,
    hasSearched,
    resultsRef: productResultsRef,
    handleSearchChange: handleProductSearchChange,
    handleKeyDown: productSearchKeyDown,
    selectProduct,
    clearSearch: clearProductSearch,
    hideResults: hideProductResults,
    showResultsDropdown: showProductResultsDropdown,
  } = useProductSearch(isEditingItems ? shopId : null);

  const canEdit = hasPermission('edit_ecom_order');
  const canChangeUnitPrice = hasPermission('change_unit_price');
  const canGiveDiscount = hasPermission('give_discount');
  const canChangeDeliveryCharge = hasPermission('change_delivery_charge');
  const isDispatched = localOrder.OrderStatus?.isDispatched;

  const footerRef = useRef(null);

  // ── Derived totals for the items edit panel ──
  const editTotals = useMemo(() => calculateOrderTotals(editItems, editDeliveryCharge), [editItems, editDeliveryCharge]);

  // ── Has anything actually changed? (gate for Save button) ──
  const itemsChanged = useMemo(() => {
    if (!originalItemsSnapshotRef.current) return false;
    return JSON.stringify(editItems.map(i => ({ productId: i.productId, variationId: i.variationId, quantity: i.quantity, unitPrice: i.unitPrice, lineDiscount: i.lineDiscount, isCrossSell: !!i.isCrossSell, crossSellQuantity: i.isCrossSell ? i.crossSellQuantity : null })))
      !== JSON.stringify(originalItemsSnapshotRef.current);
  }, [editItems]);

  const deliveryChargeChanged = useMemo(() => {
    if (originalDeliveryChargeSnapshotRef.current === null) return false;
    return editDeliveryCharge !== originalDeliveryChargeSnapshotRef.current;
  }, [editDeliveryCharge]);

  const hasItemsChanges = itemsChanged || deliveryChargeChanged;

  // ─── Auto-apply offer delivery overrides when items change ────────────────
  useEffect(() => {
    if (!isEditingItems) return;
    const offerOverrides = editItems
      .map(item => (item.offerDeliveryOverride != null ? parseFloat(item.offerDeliveryOverride) : null))
      .filter(v => v !== null);
    if (offerOverrides.length > 0) {
      const lowest = Math.min(...offerOverrides);
      setEditDeliveryCharge(prev => prev === lowest ? prev : lowest);
      return;
    }
    // No offer overrides — restore base zone delivery charge if it has been fetched
    if (baseDeliveryChargeRef.current !== null) {
      const weight = parseFloat(localOrder.weight) || 1;
      const computed = computeDeliveryWithWeight(
        baseDeliveryChargeRef.current,
        extraChargePerKgRef.current || 0,
        weight,
      );
      setEditDeliveryCharge(prev => prev === computed ? prev : computed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItems, isEditingItems]);

  // ─── Open items edit mode ─────────────────────────────────────────────────
  const openItemsEdit = useCallback(() => {
    const snapshot = itemsToEditState(localOrder.orderItems);
    setEditItems(snapshot);
    originalItemsSnapshotRef.current = snapshot.map(i => ({ productId: i.productId, variationId: i.variationId, quantity: i.quantity, unitPrice: i.unitPrice, lineDiscount: i.lineDiscount, isCrossSell: !!i.isCrossSell, crossSellQuantity: i.isCrossSell ? i.crossSellQuantity : null }));
    const dc = parseFloat(localOrder.deliveryCharge || 0);
    setEditDeliveryCharge(dc);
    originalDeliveryChargeSnapshotRef.current = dc;
    setIsEditingItems(true);
    setTimeout(() => productSearchInputRef.current?.focus(), 100);

    // Fetch base delivery charge so we can restore it if offer overrides are removed
    baseDeliveryChargeRef.current = null;
    extraChargePerKgRef.current = null;
    const currentShopId = localOrder.Shop?.id || localOrder.shopId;
    const currentCityId = localOrder.cityId || localOrder.City?.id;
    if (currentShopId && currentCityId) {
      getDeliveryCharge(currentShopId, currentCityId)
        .then(res => {
          baseDeliveryChargeRef.current = res.deliveryCharge.amount;
          extraChargePerKgRef.current = res.deliveryCharge.extraChargePerKg || 0;
        })
        .catch(() => {
          baseDeliveryChargeRef.current = 0;
          extraChargePerKgRef.current = 0;
        });
    }
  }, [localOrder]);

  const cancelItemsEdit = useCallback(() => {
    setIsEditingItems(false);
    setEditItems([]);
    clearProductSearch();
  }, [clearProductSearch]);

  // ─── Product select ───────────────────────────────────────────────────────
  const handleProductSelect = useCallback((product) => {
    selectProduct(product);
    clearProductSearch();
    setEditItems(prev => {
      if (product.type === 'bundle') {
        const existingGroup = prev.find(i => i.bundleGroupKey && i.bundleProductId === product.productId);
        if (existingGroup) {
          return rescaleBundleRows(prev, existingGroup.bundleGroupKey, (Number(existingGroup.bundleQuantity) || 0) + 1);
        }
        const stamp = Date.now();
        return [...prev, ...explodeBundle(product, { idFactory: (i) => `new-${stamp}-${i}` })
          .map(row => ({ ...row, Product: { name: row.displayName }, ProductVariation: null, _isNew: true }))];
      }
      const key = getItemMergeKey(product);
      const existingIdx = prev.findIndex(i => getItemMergeKey(i) === key);
      if (existingIdx !== -1) {
        const updated = [...prev];
        const existing = updated[existingIdx];
        const newQty = Number(existing.quantity) + 1;
        const itemOffers = existing.offers || [];
        updated[existingIdx] = {
          ...existing,
          quantity: newQty,
          unitPrice: resolveOfferPrice(itemOffers, newQty, existing.unitPrice),
          lineDiscount: existing.manuallyDiscounted
            ? existing.lineDiscount
            : resolveOfferLineDiscount(itemOffers, newQty, existing.unitPrice),
          offerDeliveryOverride: resolveOfferDelivery(itemOffers, newQty),
        };
        return updated;
      }
      const itemOffers = product.offers || [];
      const basePrice = parseFloat(product.price);
      return [...prev, {
        id: `new-${Date.now()}`,
        productId: product.productId,
        variationId: product.variationId || null,
        displayName: product.displayName + (product.attributeDisplay ? ` - ${product.attributeDisplay}` : ''),
        sku: product.sku || '',
        quantity: 1,
        unitPrice: resolveOfferPrice(itemOffers, 1, basePrice),
        lineDiscount: resolveOfferLineDiscount(itemOffers, 1, basePrice),
        manuallyDiscounted: false,
        Product: { name: product.displayName, stock: product.stockQuantity },
        ProductVariation: product.variationId ? { sku: product.sku, attributeDisplay: product.attributeDisplay } : null,
        _isNew: true,
        offers: itemOffers,
        offerDeliveryOverride: resolveOfferDelivery(itemOffers, 1),
      }];
    });
    setTimeout(() => productSearchInputRef.current?.focus(), 80);
  }, [selectProduct, clearProductSearch]);

  const handleProductKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      e.preventDefault();
      const p = productSelectedIndex >= 0 ? searchResults[productSelectedIndex] : searchResults[0];
      handleProductSelect(p);
      return;
    }
    const picked = productSearchKeyDown(e);
    if (picked) handleProductSelect(picked);
  }, [searchResults, productSelectedIndex, productSearchKeyDown, handleProductSelect]);

  // ─── Edit item field helpers ──────────────────────────────────────────────
  const updateEditItem = useCallback((itemId, field, value, meta = {}) => {
    setEditItems(prev => prev.map(i => {
      if (i.id !== itemId) return i;
      if (i.bundleGroupKey && (field === 'unitPrice' || field === 'lineDiscount')) return i;
      const updated = { ...i, [field]: value };
      if (field === 'quantity') {
        const newQty = parseInt(value) || 1;
        const itemOffers = i.offers || [];
        updated.unitPrice = resolveOfferPrice(itemOffers, newQty, i.unitPrice);
        if (!i.manuallyDiscounted) {
          updated.lineDiscount = resolveOfferLineDiscount(itemOffers, newQty, i.unitPrice);
        }
        updated.offerDeliveryOverride = resolveOfferDelivery(itemOffers, newQty);
        if (i.isCrossSell && i.crossSellQuantity > newQty) {
          updated.crossSellQuantity = newQty;
        }
        const recomputedCap = getCrossSellMaxQty({ ...i, quantity: newQty }, { currentUserId, createdByUserId });
        if (i.isCrossSell && recomputedCap <= 0) {
          updated.isCrossSell = false;
        }
      } else if (field === 'lineDiscount') {
        updated.manuallyDiscounted = true;
      } else if (field === 'isCrossSell') {
        const cap = meta.maxCrossSellQty ?? i.quantity;
        const defaultQty = i.crossSellQuantity != null ? i.crossSellQuantity : Math.min(i.quantity, cap);
        updated.crossSellQuantity = value
          ? Math.max(1, Math.min(defaultQty, i.quantity, cap))
          : i.crossSellQuantity;
      } else if (field === 'crossSellQuantity') {
        const cap = meta.maxCrossSellQty ?? i.quantity;
        updated.crossSellQuantity = Math.min(Math.max(parseInt(value) || 1, 1), cap);
      }
      return updated;
    }));
  }, [currentUserId, createdByUserId]);

  const removeEditItem = useCallback((itemId) => {
    setEditItems(prev => prev.filter(i => i.id !== itemId));
  }, []);

  const updateEditBundleQuantity = useCallback((groupKey, qty) => {
    const newQty = parseInt(qty);
    if (!newQty || newQty < 1) return;
    setEditItems(prev => rescaleBundleRows(prev, groupKey, newQty));
  }, []);

  const removeEditBundle = useCallback((groupKey) => {
    setEditItems(prev => prev.filter(i => i.bundleGroupKey !== groupKey));
  }, []);

  const setEditBundleCrossSell = useCallback((groupKey, enabled, bundlesN) => {
    setEditItems(prev => applyBundleCrossSell(prev, groupKey, enabled, Math.max(1, parseInt(bundlesN) || 1)));
  }, []);

  const handleNumberInput = useCallback((value, field, itemId) => {
    if (value === '' || value === null || value === undefined) {
      updateEditItem(itemId, field, 0);
      return;
    }
    const s = value.toString();
    if (s.endsWith('.') || s.endsWith('.0')) return;
    const n = parseFloat(s);
    if (!isNaN(n)) updateEditItem(itemId, field, n);
  }, [updateEditItem]);

  // ─── Save items ───────────────────────────────────────────────────────────
  const handleSaveItems = async () => {
    if (editItems.length === 0) { showError('At least one order item is required'); return; }
    const hasInvalid = editItems.some(i => !i.quantity || i.quantity <= 0 || (!i.bundleGroupKey && (!i.unitPrice || i.unitPrice <= 0)));
    if (hasInvalid) { showError('All items must have a valid quantity and unit price'); return; }

    const payload = {};

    if (itemsChanged) {
      payload.orderItems = editItems.map(i => ({
        id: typeof i.id === 'number' ? i.id : undefined,
        productId: i.productId,
        variationId: i.variationId || null,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        lineDiscount: i.lineDiscount || 0,
        ...(i.bundleGroupKey && {
          bundleProductId: i.bundleProductId,
          bundleGroupKey: i.bundleGroupKey,
          bundleQuantity: i.bundleQuantity,
        }),
        ...(crossSellEnabled && i.isCrossSell && {
          isCrossSell: true,
          crossSellQuantity: i.crossSellQuantity || i.quantity,
        }),
      }));
    }
    if (deliveryChargeChanged) {
      payload.deliveryCharge = editDeliveryCharge;
    }

    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, payload);

      // Re-fetch the authoritative order so cross-sell ownership/baseline (server-computed) is correct
      const { order: freshOrder } = await getEcomOrder(localOrder.id);
      setLocalOrder(freshOrder);
      onUpdate(freshOrder);
      setIsEditingItems(false);
      clearProductSearch();
      success('Order items updated successfully');
    } catch (err) {
      console.error('Failed to update order items:', err);
      showError(err.response?.data?.message || 'Failed to update order items');
    } finally {
      setIsUpdating(false);
    }
  };

  // ─── Customer name handlers ───────────────────────────────────────────────
  const handleSaveCustomerName = async () => {
    if (!customerNameValue.trim()) { showError('Customer name is required'); return; }
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, { customerName: customerNameValue.trim() });
      const updatedOrder = { ...localOrder, customerName: customerNameValue.trim() };
      setLocalOrder(updatedOrder);
      onUpdate(updatedOrder);
      setIsEditingCustomerName(false);
      success('Customer name updated successfully');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update customer name');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelCustomerName = () => {
    setCustomerNameValue(localOrder.customerName || '');
    setIsEditingCustomerName(false);
  };

  // ─── Phone number edit handlers ───────────────────────────────────────────
  const handleOpenPhoneEdit = () => {
    const phones = (localOrder.phoneNumbers || []).map(p => ({
      phoneNumber: p.phoneNumber,
      isPrimary: p.isPrimary,
    }));
    setEditPhoneNumbers(phones.length > 0 ? phones : [{ phoneNumber: '', isPrimary: true }]);
    setPhoneValidationErrors({});
    setIsEditingPhoneNumbers(true);
  };

  const handleCancelPhoneEdit = () => {
    setEditPhoneNumbers([]);
    setPhoneValidationErrors({});
    setIsEditingPhoneNumbers(false);
  };

  const handlePhoneChange = (index, value) => {
    setEditPhoneNumbers(prev => prev.map((p, i) => i === index ? { ...p, phoneNumber: value } : p));
  };

  const handlePhoneValidationChange = (index, hasError, errorMessage) => {
    setPhoneValidationErrors(prev => {
      const next = { ...prev };
      if (hasError) next[index] = errorMessage;
      else delete next[index];
      return next;
    });
  };

  const handleAddEditPhone = () => {
    setEditPhoneNumbers(prev => [...prev, { phoneNumber: '', isPrimary: false }]);
  };

  const handleRemoveEditPhone = (index) => {
    setEditPhoneNumbers(prev => {
      const next = prev.filter((_, i) => i !== index);
      // If the removed phone was primary, assign primary to first remaining
      if (prev[index]?.isPrimary && next.length > 0) {
        next[0] = { ...next[0], isPrimary: true };
      }
      return next;
    });
    setPhoneValidationErrors(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki < index) next[k] = prev[k];
        else if (ki > index) next[ki - 1] = prev[k];
      });
      return next;
    });
  };

  const handleSavePhoneNumbers = async () => {
    const filled = editPhoneNumbers.filter(p => p.phoneNumber.trim());
    if (filled.length === 0) { showError('At least one phone number is required'); return; }
    if (Object.keys(phoneValidationErrors).length > 0) { showError('Please fix phone number errors before saving'); return; }
    const hasPrimary = filled.some(p => p.isPrimary);
    if (!hasPrimary) { showError('Please set one phone number as primary'); return; }

    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, { phoneNumbers: filled });
      // Preserve previousOrders count and blacklist status from original data where possible
      const updatedPhones = filled.map((p, i) => ({
        ...p,
        previousOrders: localOrder.phoneNumbers?.find(op => op.phoneNumber === p.phoneNumber)?.previousOrders || 0,
        isBlacklisted: localOrder.phoneNumbers?.find(op => op.phoneNumber === p.phoneNumber)?.isBlacklisted || false,
      }));
      const updatedOrder = { ...localOrder, phoneNumbers: updatedPhones };
      setLocalOrder(updatedOrder);
      onUpdate(updatedOrder);
      setIsEditingPhoneNumbers(false);
      setPhoneValidationErrors({});
      success('Phone numbers updated successfully');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update phone numbers');
    } finally {
      setIsUpdating(false);
    }
  };

  // ─── Address / notes handlers ─────────────────────────────────────────────
  useEffect(() => {
    if (isEditingAddress) {
      citySearch.initSearch(localOrder.City?.name || '');
      setSelectedCityId(localOrder.cityId || null);
      setSelectedCityName(localOrder.City?.name || '');
      setAddressValue(localOrder.address || '');
      setAddressError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingAddress]);

  useEffect(() => {
    const handler = (e) => { if (footerRef.current && !footerRef.current.contains(e.target)) setExpandedStatusKey(null); };
    if (expandedStatusKey) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expandedStatusKey]);

  useEffect(() => {
    const handler = (e) => { if (footerRef.current && !footerRef.current.contains(e.target)) setConfirmingStatusKey(null); };
    if (confirmingStatusKey) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [confirmingStatusKey]);

  const isCityUnknown = (o) => o.cityId === CITY_UNKNOWN_ID || o.City?.id === CITY_UNKNOWN_ID;

  const isPreviousOrderCancellable = (prevOrder) => {
    if (cancelledOrderIds.has(prevOrder.id)) return false;
    const s = prevOrder.OrderStatus;
    return s && !s.isInPacking && !s.isDispatched && s.key !== 'cancelled';
  };

  const getStatusMode = (status) => {
    if (status.requiresReason && !status.requiresHoldDate) return 'inline-reason';
    if (status.requiresHoldDate) return 'legacy-dropdown';
    return 'direct';
  };

  const handleDirectStatusUpdate = async (statusKey) => {
    if (statusKey === 'confirmed' && isCityUnknown(localOrder)) { showError('Cannot confirm order with incomplete address. Please update the city first.'); setConfirmingStatusKey(null); return; }
    setIsUpdating(true); setError('');
    try { await onStatusUpdate(localOrder.id, { orderStatusKey: statusKey }); setConfirmingStatusKey(null); }
    catch (err) { setError(err.message || t('orderAssignment.failedToUpdateStatus')); }
    finally { setIsUpdating(false); }
  };

  const handleReasonSelect = async (statusKey, reasonKey) => {
    if (statusKey === 'confirmed' && isCityUnknown(localOrder)) { showError('Cannot confirm order with incomplete address. Please update the city first.'); return; }
    setIsUpdating(true); setError('');
    try { await onStatusUpdate(localOrder.id, { orderStatusKey: statusKey, orderStatusReasonKey: reasonKey }); setExpandedStatusKey(null); }
    catch (err) { setError(err.message || t('orderAssignment.failedToUpdateStatus')); }
    finally { setIsUpdating(false); }
  };

  const handleLegacyStatusClick = (statusKey) => {
    if (statusKey === 'confirmed' && isCityUnknown(localOrder)) { showError('Cannot confirm order with incomplete address. Please update the city first.'); return; }
    setExpandedStatusKey(expandedStatusKey === statusKey ? null : statusKey);
    setConfirmingStatusKey(null);
    setSelectedReason(''); setHoldUntilDate('');
  };

  const handleStatusWithReasonSubmit = async (statusKey) => {
    const statusObj = availableStatuses.find(s => s.key === statusKey);
    setError('');
    if (statusKey === 'confirmed' && isCityUnknown(localOrder)) { showError('Cannot confirm order with incomplete address. Please update the city first.'); return; }
    if (statusObj.requiresReason && !selectedReason) { setError(t('orderAssignment.pleaseSelectReason')); return; }
    if (statusObj.requiresHoldDate && !holdUntilDate) { setError(t('orderAssignment.pleaseSelectHoldDate')); return; }
    setIsUpdating(true);
    try {
      await onStatusUpdate(localOrder.id, { orderStatusKey: statusKey, orderStatusReasonKey: statusObj.requiresReason ? selectedReason : null, holdUntilDate: statusObj.requiresHoldDate ? holdUntilDate : null });
      setExpandedStatusKey(null);
    } catch (err) { setError(err.message || t('orderAssignment.failedToUpdateStatus')); }
    finally { setIsUpdating(false); }
  };

  const loadPreviousOrders = async (phoneNumber) => {
    setLoadingPreviousOrders(true); setSelectedPhoneNumber(phoneNumber);
    try {
      const thirtyDaysAgo = new Date(); thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const response = await getEcomOrders({ search: phoneNumber, fromDate: thirtyDaysAgo.toISOString().split('T')[0], limit: 50 });
      setPreviousOrders(response.orders.filter(o => o.id !== localOrder.id));
      setShowPreviousOrders(true);
    } catch (err) { console.error('Failed to load previous orders:', err); setError('Failed to load previous orders'); }
    finally { setLoadingPreviousOrders(false); }
  };

  const handleCancelAsDuplicate = async (prevOrder) => {
    setCancellingOrderIds(prev => new Set(prev).add(prevOrder.id));
    try {
      await updateEcomOrder(prevOrder.id, { orderStatusKey: 'cancelled', orderStatusReasonKey: 'duplicate' });
      setCancelledOrderIds(prev => new Set(prev).add(prevOrder.id));
      setPreviousOrders(prev => prev.map(o => o.id === prevOrder.id
        ? { ...o, OrderStatus: { ...(o.OrderStatus || {}), key: 'cancelled', label: 'Cancelled', colorCode: '#EF4444', isInPacking: false, isDispatched: false } } : o));
      success('Order cancelled as duplicate');
    } catch (err) { showError(err.response?.data?.message || 'Failed to cancel order'); }
    finally { setCancellingOrderIds(prev => { const n = new Set(prev); n.delete(prevOrder.id); return n; }); }
  };

  const handleToggleUrgent = async () => {
    if (!canEdit || isDispatched) return;
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, { isUrgent: !localOrder.isUrgent });
      const updatedOrder = { ...localOrder, isUrgent: !localOrder.isUrgent };
      setLocalOrder(updatedOrder); onUpdate(updatedOrder);
      success(localOrder.isUrgent ? 'Order unmarked as urgent' : 'Order marked as urgent');
    } catch (err) { showError('Failed to update urgent status'); }
    finally { setIsUpdating(false); }
  };

  const handleSaveDeliveryNotes = async () => {
    if (deliveryNotesValue.length > 40) { showError('Delivery notes cannot exceed 40 characters'); return; }
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, { deliveryNotes: deliveryNotesValue });
      const updatedOrder = { ...localOrder, deliveryNotes: deliveryNotesValue };
      setLocalOrder(updatedOrder); onUpdate(updatedOrder); setIsEditingDeliveryNotes(false);
      success('Delivery notes updated successfully');
    } catch (err) { showError('Failed to update delivery notes'); }
    finally { setIsUpdating(false); }
  };

  const handleSaveSpecialNotes = async () => {
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, { specialNotes: specialNotesValue });
      const updatedOrder = { ...localOrder, specialNotes: specialNotesValue };
      setLocalOrder(updatedOrder); onUpdate(updatedOrder); setIsEditingSpecialNotes(false);
      success('Special notes updated successfully');
    } catch (err) { showError('Failed to update special notes'); }
    finally { setIsUpdating(false); }
  };

  const handleCancelDeliveryNotes = () => { setDeliveryNotesValue(localOrder.deliveryNotes || ''); setIsEditingDeliveryNotes(false); };
  const handleCancelSpecialNotes  = () => { setSpecialNotesValue(localOrder.specialNotes  || ''); setIsEditingSpecialNotes(false); };

  const handleCitySelect = useCallback((city) => {
    setSelectedCityId(city.id); setSelectedCityName(city.name); citySearch.initSearch(city.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAddress = async () => {
    setAddressError('');
    if (!addressValue.trim()) { setAddressError('Address is required'); return; }
    if (!selectedCityId)      { setAddressError('Please select a city'); return; }
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, { address: addressValue.trim(), cityId: selectedCityId });
      const cityObj = cities.find(c => c.id === selectedCityId);
      const updatedOrder = { ...localOrder, address: addressValue.trim(), cityId: selectedCityId, City: cityObj ? { id: cityObj.id, name: cityObj.name } : localOrder.City };
      setLocalOrder(updatedOrder); onUpdate(updatedOrder); setIsEditingAddress(false);
      success('Address updated successfully');
    } catch (err) { showError(err.response?.data?.message || 'Failed to update address'); }
    finally { setIsUpdating(false); }
  };

  const handleCancelAddress = () => {
    setAddressValue(localOrder.address || ''); setSelectedCityId(localOrder.cityId || null);
    setSelectedCityName(localOrder.City?.name || ''); setAddressError(''); setIsEditingAddress(false);
  };

  const handleViewOrder = () => window.open(`/orders/${localOrder.id}`, '_blank');
  const handleEditOrder = () => window.open(`/orders/${localOrder.id}/edit`, '_blank');

  // ─── Stock badge helper ───────────────────────────────────────────────────
  const getStockStatus = (stock) => {
    if (stock === undefined || stock === null) return { text: t('common.na'), className: styles.stockNA };
    if (stock <= 0)  return { text: t('common.outOfStock'), className: styles.stockOut };
    if (stock < 10)  return { text: `${stock} ${t('common.inStock')}`, className: styles.stockLow };
    return { text: `${stock} ${t('common.inStock')}`, className: styles.stockAvailable };
  };

  // ─── Render: product search results ──────────────────────────────────────
  const renderProductSearchResults = () => {
    if (!showProductResults) return null;
    return (
      <div className={styles.itemsSearchResults} ref={productResultsRef}>
        {isProductSearching ? (
          <div className={styles.itemsSearchLoading}><Loader2 size={14} className={styles.spinIcon} /><span>{t('common.loading')}</span></div>
        ) : searchResults.length > 0 ? (
          <div className={styles.itemsResultsList}>
            {searchResults.map((product, index) => (
              <div
                key={getItemMergeKey(product)}
                className={`${styles.itemsResultItem} ${index === productSelectedIndex ? styles.itemsResultSelected : ''} ${product.isOutOfStock ? styles.itemsResultOutOfStock : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleProductSelect(product); }}
              >
                <div className={styles.itemsResultInfo}>
                  <div className={styles.itemsResultName}>{product.formattedName}{product.type === 'bundle' && <> <BundleBadge size={10} /></>}</div>
                  <div className={styles.itemsResultMeta}>
                    {product.sku && <span className={styles.itemsResultSku}><Hash size={11} />{product.sku}</span>}
                    {!product.stockHidden && (
                      <span className={styles.itemsResultStock}><Package size={11} />{product.formattedStock}</span>
                    )}
                  </div>
                </div>
                <div className={styles.itemsResultPrice}>{product.formattedPrice}</div>
              </div>
            ))}
          </div>
        ) : hasSearched ? (
          <div className={styles.itemsResultEmpty}><AlertCircle size={14} /><span>{t('ecomOrders.noProductsFound')}</span></div>
        ) : null}
      </div>
    );
  };

  // ─── Render: one editable item row ───────────────────────────────────────
  const renderEditItemRow = (item) => {
    const line = calculateLineTotal(item.quantity, item.unitPrice, item.lineDiscount || 0);
    const stock = item.Product?.stock ?? item.Product?.stockQuantity;
    const stockStatus = getStockStatus(stock);

    const isCrossSoldByOther = item.crossSellById != null && item.crossSellById !== currentUserId;
    const crossSellMaxQty = getCrossSellMaxQty(item, { currentUserId, createdByUserId });
    const crossSellDisabledNew = !item.isCrossSell && crossSellMaxQty <= 0;

    return (
      <div key={item.id} className={styles.editItemRow}>
        <div className={styles.editItemHeader}>
          <div className={styles.editItemNameWrap}>
            <span className={styles.editItemName}>{item.displayName}</span>
            {item.sku && <span className={styles.editItemSku}><Hash size={11} />{item.sku}</span>}
            {!isResellerDropshipper && (
              <span className={`${styles.editItemStock} ${stockStatus.className}`}>
                <Package size={11} />{stockStatus.text}
              </span>
            )}
            {item._isNew && <span className={styles.editItemNewBadge}>New</span>}
          </div>
          <button
            type="button"
            onClick={() => removeEditItem(item.id)}
            className={styles.editItemRemoveBtn}
            disabled={isCrossSoldByOther}
            title={isCrossSoldByOther
              ? (t('ecomOrders.cannotRemoveOthersCrossSell') || 'Only the cross-sell owner can remove this item')
              : 'Remove item'}
          >
            <X size={15} />
          </button>
        </div>
        <div className={`${styles.editItemFields} ${crossSellEnabled ? styles.editItemFieldsWithCrossSell : ''}`}>
          {/* Quantity */}
          <div className={styles.editItemFieldGroup}>
            <label>{t('ecomOrders.quantity')}</label>
            <div className={styles.qtyWrapper}>
              <button type="button" className={styles.qtyBtn} onClick={() => updateEditItem(item.id, 'quantity', Math.max(1, Number(item.quantity) - 1), { currentUserId, createdByUserId })} disabled={Number(item.quantity) <= 1 || isCrossSoldByOther}><Minus size={13} /></button>
              <input
                type="number" min="1" step="1"
                value={Math.round(item.quantity)}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v) && v >= 1) updateEditItem(item.id, 'quantity', v, { currentUserId, createdByUserId });
                }}
                onKeyDown={(e) => { if (['.', ',', '-', 'e'].includes(e.key)) e.preventDefault(); }}
                disabled={isCrossSoldByOther}
                className={styles.qtyInput}
              />
              <button type="button" className={styles.qtyBtn} onClick={() => updateEditItem(item.id, 'quantity', Number(item.quantity) + 1, { currentUserId, createdByUserId })} disabled={isCrossSoldByOther}><Plus size={13} /></button>
            </div>
          </div>

          {/* Unit Price */}
          <div className={styles.editItemFieldGroup}>
            <label>{t('ecomOrders.unitPrice')} {!canChangeUnitPrice && <span className={styles.lockedIcon}>🔒</span>}</label>
            <div className={styles.editItemPriceWrap}>
              <DollarSign size={13} className={styles.editItemPriceIcon} />
              <input
                type="text"
                value={formatNum(item.unitPrice)}
                onChange={(e) => handleNumberInput(e.target.value, 'unitPrice', item.id)}
                disabled={!canChangeUnitPrice || isCrossSoldByOther}
                className={styles.editItemPriceInput}
              />
            </div>
          </div>

          {/* Discount */}
          <div className={styles.editItemFieldGroup}>
            <label>{t('ecomOrders.lineDiscount')} {!canGiveDiscount && <span className={styles.lockedIcon}>🔒</span>}</label>
            <div className={styles.discountFieldRow}>
              <div className={styles.editItemPriceWrap}>
                <DollarSign size={13} className={styles.editItemPriceIcon} />
                <input
                  type="text"
                  value={formatNum(item.lineDiscount || 0)}
                  onChange={(e) => handleNumberInput(e.target.value, 'lineDiscount', item.id)}
                  disabled={!canGiveDiscount || isCrossSoldByOther}
                  className={styles.editItemPriceInput}
                  placeholder="0"
                />
              </div>
              {canGiveDiscount && (
                <button
                  type="button"
                  className={`${styles.discountPctBtn} ${discountPopupState?.itemId === item.id ? styles.discountPctBtnActive : ''}`}
                  onClick={(e) => setDiscountPopupState(
                    discountPopupState?.itemId === item.id
                      ? null
                      : { itemId: item.id, anchorEl: e.currentTarget }
                  )}
                  title="Set discount as percentage"
                >
                  <Percent size={11} />
                </button>
              )}
              {discountPopupState?.itemId === item.id && (
                <DiscountPercentagePopup
                  anchorEl={discountPopupState.anchorEl}
                  unitPrice={item.unitPrice}
                  quantity={item.quantity}
                  onApply={(amount) => updateEditItem(item.id, 'lineDiscount', amount)}
                  onClose={() => setDiscountPopupState(null)}
                />
              )}
            </div>
          </div>

          {/* Line total */}
          <div className={styles.editItemFieldGroup}>
            <label>{t('ecomOrders.lineTotal')}</label>
            <div className={styles.editItemLineTotal}>{formatCurrency(line.lineTotal)}</div>
          </div>

          {crossSellEnabled && (
            <div className={styles.editItemFieldGroup}>
              <label title={crossSellDisabledNew ? (t('ecomOrders.crossSellNoEligibleQty') || 'No new/increased quantity to cross-sell') : undefined}>
                {t('ecomOrders.crossSellItem') || 'Cross Sell'}
              </label>
              <div className={styles.crossSellControl}>
                <div className={styles.crossSellCheckboxBox}>
                  <input
                    type="checkbox"
                    className={styles.crossSellCheckbox}
                    checked={!!item.isCrossSell}
                    disabled={isCrossSoldByOther || crossSellDisabledNew}
                    onChange={(e) => updateEditItem(item.id, 'isCrossSell', e.target.checked, { maxCrossSellQty: crossSellMaxQty })}
                  />
                </div>
                <select
                  className={styles.crossSellQtyCompact}
                  value={item.isCrossSell ? (Number(item.crossSellQuantity) || 1) : 0}
                  onChange={(e) => updateEditItem(item.id, 'crossSellQuantity', parseInt(e.target.value), { maxCrossSellQty: crossSellMaxQty })}
                  disabled={!item.isCrossSell || isCrossSoldByOther}
                >
                  {item.isCrossSell
                    ? Array.from({ length: Math.max(item.crossSellQuantity || 0, crossSellMaxQty) }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))
                    : <option value={0}>0</option>}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Render: full items edit block ───────────────────────────────────────
  const renderItemsEditBlock = () => (
    <div className={styles.itemsEditBlock}>
      {/* Product search */}
      <div className={styles.itemsSearchSection}>
        <div className={styles.itemsSearchWrapper}>
          <div className={styles.itemsSearchInputWrap}>
            <Search size={15} className={styles.itemsSearchIcon} />
            <input
              ref={productSearchInputRef}
              type="text"
              value={productSearchTerm}
              onChange={(e) => handleProductSearchChange(e.target.value)}
              onKeyDown={handleProductKeyDown}
              onFocus={showProductResultsDropdown}
              onBlur={() => setTimeout(hideProductResults, 200)}
              placeholder={t('ecomOrders.searchProductsPlaceholder') || 'Search products to add…'}
              className={styles.itemsSearchInput}
              autoComplete="off"
            />
            {productSearchTerm && (
              <button type="button" onClick={clearProductSearch} className={styles.itemsSearchClearBtn}><X size={14} /></button>
            )}
          </div>
          {renderProductSearchResults()}
        </div>
      </div>

      {/* Items list */}
      {editItems.length === 0 ? (
        <div className={styles.itemsEditEmpty}>
          <Package size={32} />
          <p>No items — search and add products above</p>
        </div>
      ) : (
        <div className={styles.itemsEditList}>
          {groupOrderItems(editItems).map((entry, index) => (entry.type === 'bundle' ? (
            <BundleItemCard
              key={entry.groupKey}
              group={entry}
              onQuantityChange={(qty) => updateEditBundleQuantity(entry.groupKey, qty)}
              onRemove={() => removeEditBundle(entry.groupKey)}
              crossSell={crossSellEnabled ? {
                checked: !!entry.items[0]?.isCrossSell,
                count: getBundleCrossSellCount(entry.items),
                maxQty: getBundleCrossSellMax(entry.items, { currentUserId, createdByUserId }),
                lockedByOther: entry.items[0]?.crossSellById != null && entry.items[0].crossSellById !== currentUserId,
                onChange: (enabled, n) => setEditBundleCrossSell(entry.groupKey, enabled, n),
              } : null}
            />
          ) : renderEditItemRow(entry.item, index)))}
        </div>
      )}

      {/* Delivery charge + totals footer */}
      <div className={styles.itemsEditFooter}>
        <div className={styles.itemsEditTotalsGrid}>
          <div className={styles.itemsEditTotalRow}>
            <span className={styles.itemsEditTotalLabel}><Receipt size={13} />Subtotal</span>
            <span className={styles.itemsEditTotalValue}>{formatCurrency(editTotals.totalAmount)}</span>
          </div>
          {editTotals.totalDiscount > 0 && (
            <div className={styles.itemsEditTotalRow}>
              <span className={styles.itemsEditTotalLabel}><Receipt size={13} />Discount</span>
              <span className={styles.itemsEditTotalDiscount}>-{formatCurrency(editTotals.totalDiscount)}</span>
            </div>
          )}
          <div className={styles.itemsEditTotalRow}>
            <span className={styles.itemsEditTotalLabel}><Truck size={13} />Delivery Charge {!canChangeDeliveryCharge && <span className={styles.lockedIcon}>🔒</span>}</span>
            {canChangeDeliveryCharge ? (
              <div className={styles.itemsDeliveryEditWrap}>
                <DollarSign size={12} className={styles.itemsDeliveryIcon} />
                <input
                  type="text"
                  value={formatNum(editDeliveryCharge)}
                  onChange={(e) => {
                    const s = e.target.value;
                    if (s.endsWith('.') || s.endsWith('.0')) return;
                    const n = parseFloat(s);
                    if (!isNaN(n) && n >= 0) setEditDeliveryCharge(n);
                    else if (s === '' || s === '0') setEditDeliveryCharge(0);
                  }}
                  className={styles.itemsDeliveryInput}
                />
              </div>
            ) : (
              <span className={styles.itemsEditTotalValue}>{formatCurrency(editDeliveryCharge)}</span>
            )}
          </div>
          <div className={`${styles.itemsEditTotalRow} ${styles.itemsEditGrandTotalRow}`}>
            <span className={styles.itemsEditGrandTotalLabel}><DollarSign size={14} />Grand Total</span>
            <span className={styles.itemsEditGrandTotalValue}>{formatCurrency(editTotals.grandTotal)}</span>
          </div>
        </div>

        <div className={styles.editActions}>
          <button
            className={styles.saveButton}
            onClick={handleSaveItems}
            disabled={isUpdating || !hasItemsChanges || editItems.length === 0}
            title={!hasItemsChanges ? 'No changes to save' : undefined}
          >
            <Save size={14} />
            {isUpdating ? 'Saving…' : 'Save'}
          </button>
          <button className={styles.cancelEditButton} onClick={cancelItemsEdit} disabled={isUpdating}>
            <CancelIcon size={14} />Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Render: read-only order items ───────────────────────────────────────
  const renderItemsReadOnly = () => (
    <div className={styles.compactItemsList}>
      {groupOrderItems(localOrder.orderItems || []).map((entry, gIdx) => (entry.type === 'bundle' ? (
        <BundleItemGroup
          key={entry.groupKey}
          group={entry}
          variant="compact"
          renderItem={(item, idx) => renderReadOnlyItem(item, `${entry.groupKey}-${idx}`)}
        />
      ) : renderReadOnlyItem(entry.item, gIdx)))}
    </div>
  );

  const renderReadOnlyItem = (item, index) => {
        const stock = item.Product?.stock;
        const stockStatus = getStockStatus(stock);
        return (
          <div key={index} className={styles.compactOrderItem}>
            <div className={styles.itemInfo}>
              <div className={styles.itemName}>{item.Product?.name}</div>
              <div className={styles.itemMeta}>
                {item.ProductVariation && <span className={styles.itemVariation}>{t('orderAssignment.sku')}: {item.ProductVariation.sku}</span>}
                {!isResellerDropshipper && (
                  <span className={`${styles.itemStock} ${stockStatus.className}`}><Package size={12} />{stockStatus.text}</span>
                )}
              </div>
            </div>
            <div className={styles.itemQtyPrice}>
              <span className={styles.itemQuantity}>× {item.quantity}</span>
              <span className={styles.itemPrice}>
                {item.bundleGroupKey && Number(item.lineTotal) <= 0 && (
                  <span className={styles.freeTag}>{t('ecomOrders.free') || 'FREE'}</span>
                )}
                {formatCurrency(item.lineTotal)}
              </span>
            </div>
          </div>
        );
  };

  // ─── Render: phone numbers (read-only + edit mode) ────────────────────────
  const renderPhoneNumbers = () => {
    if (isEditingPhoneNumbers) {
      return (
        <div className={styles.phoneEditBlock}>
          <div className={styles.phoneEditList}>
            {editPhoneNumbers.map((phone, index) => (
              <div key={index} className={styles.phoneEditItem}>
                <div className={styles.phoneEditInputWrap}>
                  <PhoneNumberInput
                    id={`edit-phone-${index}`}
                    value={phone.phoneNumber}
                    onChange={(value) => handlePhoneChange(index, value)}
                    onValidationChange={(hasError, msg) => handlePhoneValidationChange(index, hasError, msg)}
                    placeholder="Enter phone number"
                    required={index === 0}
                  />
                </div>
                {editPhoneNumbers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEditPhone(index)}
                    className={styles.phoneRemoveBtn}
                    title="Remove phone number"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={handleAddEditPhone} className={styles.phoneAddBtn}>
            <PlusIcon size={13} />Add phone number
          </button>
          <div className={styles.editActions}>
            <button className={styles.saveButton} onClick={handleSavePhoneNumbers} disabled={isUpdating}>
              <Save size={14} />{isUpdating ? 'Saving…' : 'Save'}
            </button>
            <button className={styles.cancelEditButton} onClick={handleCancelPhoneEdit} disabled={isUpdating}>
              <CancelIcon size={14} />Cancel
            </button>
          </div>
        </div>
      );
    }

    if (!localOrder.phoneNumbers?.length) return <span className={styles.infoValue}>{t('common.na')}</span>;
    return (
      <div className={styles.phoneNumbersList}>
        {localOrder.phoneNumbers.map((phone, index) => {
          const hasPrev = phone.previousOrders > 0;
          const isBlacklisted = phone.isBlacklisted;
          const phoneHighlightClass = isBlacklisted
            ? styles.phoneBlacklisted
            : hasPrev
            ? styles.phoneWithHistory
            : '';
          return (
            <div key={index} className={styles.phoneNumberItem}>
              <a href={`tel:${phone.phoneNumber}`} className={`${styles.phoneLink} ${phoneHighlightClass}`}>
                <Phone size={14} />{phone.phoneNumber}
                {phone.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                {hasPrev && <span className={styles.previousOrdersBadge}>{phone.previousOrders}</span>}
              </a>
              {hasPrev && (
                <button onClick={() => loadPreviousOrders(phone.phoneNumber)} className={styles.viewPreviousButton} disabled={loadingPreviousOrders}>
                  <Eye size={14} />{loadingPreviousOrders && selectedPhoneNumber === phone.phoneNumber ? t('common.loading') : t('orderAssignment.viewPrevious')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Address edit UI ──────────────────────────────────────────────────────
  const renderAddressEdit = () => (
    <div className={styles.addressEditField}>
      <div className={styles.addressEditGroup}>
        <label className={styles.addressEditLabel}>Address</label>
        <textarea value={addressValue} onChange={(e) => setAddressValue(e.target.value)} className={styles.addressEditTextarea} rows={2} placeholder="Enter delivery address..." disabled={isUpdating} />
      </div>
      <div className={styles.addressEditGroup}>
        <label className={styles.addressEditLabel}>City</label>
        <div className={styles.citySearchWrapper}>
          <div className={styles.cityInputWrapper}>
            <Search size={14} className={styles.citySearchIcon} />
            <input
              ref={cityInputRef} type="text" value={citySearch.searchTerm}
              onChange={(e) => citySearch.handleSearchChange(e.target.value)}
              onKeyDown={(e) => citySearch.handleKeyDown(e, handleCitySelect)}
              onFocus={citySearch.showResultsDropdown}
              onBlur={() => setTimeout(citySearch.hideResults, 180)}
              placeholder="Search city..."
              className={`${styles.citySearchInput} ${!selectedCityId && addressError ? styles.citySearchInputError : ''}`}
              disabled={isUpdating}
            />
            {selectedCityId && citySearch.searchTerm && <span className={styles.citySelectedTick}>✓</span>}
          </div>
          {citySearch.showResults && citySearch.filteredCities.length > 0 && (
            <div className={styles.cityResultsDropdown}>
              {citySearch.filteredCities.map((city, index) => (
                <div key={city.id} className={`${styles.cityResultItem} ${index === citySearch.selectedIndex ? styles.cityResultItemSelected : ''}`} onMouseDown={(e) => { e.preventDefault(); handleCitySelect(city); }}>
                  <span className={styles.cityResultName}>{city.name}</span>
                  {city.District?.name && <span className={styles.cityResultDistrict}>{city.District.name}</span>}
                </div>
              ))}
            </div>
          )}
          {citySearch.showResults && citySearch.searchTerm.trim() && !citySearch.filteredCities.length && (
            <div className={styles.cityResultsDropdown}><div className={styles.cityResultsEmpty}>No cities found</div></div>
          )}
        </div>
        {selectedCityId && selectedCityName && (
          <div className={styles.citySelectedDisplay}><MapPin size={12} /><span>{selectedCityName}</span></div>
        )}
      </div>
      {addressError && <div className={styles.addressEditError}><AlertCircle size={13} />{addressError}</div>}
      <div className={styles.editActions}>
        <button className={styles.saveButton} onClick={handleSaveAddress} disabled={isUpdating}><Save size={14} />{isUpdating ? 'Saving…' : 'Save'}</button>
        <button className={styles.cancelEditButton} onClick={handleCancelAddress} disabled={isUpdating}><CancelIcon size={14} />Cancel</button>
      </div>
    </div>
  );

  // ─── Status buttons ───────────────────────────────────────────────────────
  const renderStatusButtons = () => (
    <div className={styles.statusActions}>
      {availableStatuses.map((status) => {
        const mode = getStatusMode(status);
        const isExpanded = expandedStatusKey === status.key;
        const isCurrentStatus = localOrder.OrderStatus?.key === status.key;

        if (mode === 'inline-reason') {
          const reasons = orderStatusReasons[status.key] || [];
          return (
            <div key={status.key} className={styles.statusActionWrapper}>
              <button onClick={() => { setExpandedStatusKey(isExpanded ? null : status.key); setConfirmingStatusKey(null); }} disabled={isUpdating}
                className={`${styles.statusActionButton} ${isCurrentStatus ? styles.currentStatus : ''}`}
                style={{ backgroundColor: status.colorCode, opacity: isUpdating ? 0.6 : 1 }}>
                <span>{status.label}</span>
                <ChevronDown size={14} className={`${styles.chevron} ${isExpanded ? styles.chevronUp : ''}`} />
              </button>
              {isExpanded && (
                <div className={styles.inlineReasonMenu}>
                  <div className={styles.inlineReasonMenuLabel}>{t('orderAssignment.selectReason')}</div>
                  {reasons.length === 0 ? <div className={styles.inlineReasonEmpty}>No reasons available</div>
                    : reasons.map(reason => (
                      <button key={reason.key} className={styles.inlineReasonItem} style={{ '--reason-color': status.colorCode }} disabled={isUpdating} onClick={() => handleReasonSelect(status.key, reason.key)}>{reason.label}</button>
                    ))}
                </div>
              )}
            </div>
          );
        }

        if (mode === 'legacy-dropdown') {
          return (
            <div key={status.key} className={styles.statusActionWrapper}>
              <button onClick={() => handleLegacyStatusClick(status.key)} disabled={isUpdating}
                className={`${styles.statusActionButton} ${isCurrentStatus ? styles.currentStatus : ''}`}
                style={{ backgroundColor: status.colorCode, opacity: isUpdating ? 0.6 : 1 }}>
                <span>{status.label}</span><ChevronDown size={16} />
              </button>
              {isExpanded && (
                <div className={styles.statusDropdown}>
                  {status.requiresReason && orderStatusReasons[status.key] && (
                    <div className={styles.dropdownSection}>
                      <label>{t('orderAssignment.selectReason')} *</label>
                      <select value={selectedReason} onChange={(e) => setSelectedReason(e.target.value)} className={styles.dropdownSelect}>
                        <option value="">{t('orderAssignment.selectReason')}</option>
                        {orderStatusReasons[status.key].map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                    </div>
                  )}
                  {status.requiresHoldDate && (
                    <div className={styles.dropdownSection}>
                      <label><Calendar size={14} />{t('orderAssignment.holdUntilDate')} *</label>
                      <input type="date" value={holdUntilDate} onChange={(e) => setHoldUntilDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className={styles.dropdownInput} />
                    </div>
                  )}
                  <button onClick={() => handleStatusWithReasonSubmit(status.key)} disabled={isUpdating} className={styles.dropdownSubmit} style={{ backgroundColor: status.colorCode }}>
                    {isUpdating ? t('orderAssignment.updating') : t('common.update')}
                  </button>
                </div>
              )}
            </div>
          );
        }

        const isConfirming = confirmingStatusKey === status.key;
        return (
          <div key={status.key} className={styles.statusActionWrapper}>
            <button
              onClick={() => { setConfirmingStatusKey(isConfirming ? null : status.key); setExpandedStatusKey(null); }}
              disabled={isUpdating}
              className={`${styles.statusActionButton} ${isCurrentStatus ? styles.currentStatus : ''}`}
              style={{ backgroundColor: status.colorCode, opacity: isUpdating ? 0.6 : 1 }}>
              <span>{status.label}</span>
            </button>
            {isConfirming && (
              <div className={styles.confirmPopup}>
                <div className={styles.confirmPopupText}>
                  <AlertCircle size={14} />
                  <span>{t('orderAssignment.confirmStatusChange', { status: status.label })}</span>
                </div>
                <div className={styles.confirmPopupActions}>
                  <button
                    className={styles.confirmPopupYes}
                    disabled={isUpdating}
                    onClick={() => handleDirectStatusUpdate(status.key)}
                  >
                    {isUpdating ? t('orderAssignment.updating') : t('common.yes')}
                  </button>
                  <button
                    className={styles.confirmPopupNo}
                    disabled={isUpdating}
                    onClick={() => setConfirmingStatusKey(null)}
                  >
                    {t('common.no')}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderPreviousOrders = () => {
    if (!showPreviousOrders) return null;
    return (
      <div className={styles.previousOrdersSection}>
        <div className={styles.previousOrdersHeader}>
          <h3>{t('orderAssignment.previousOrders')} ({previousOrders.length})</h3>
          <button onClick={() => setShowPreviousOrders(false)} className={styles.closePreviousButton}><X size={16} /></button>
        </div>
        {previousOrders.length === 0 ? (
          <div className={styles.noPreviousOrders}><AlertCircle size={24} /><p>{t('orderAssignment.noPreviousOrders')}</p></div>
        ) : (
          <div className={styles.previousOrdersList}>
            {previousOrders.map((prevOrder) => {
              const isCancellable = isPreviousOrderCancellable(prevOrder);
              const isCancelling  = cancellingOrderIds.has(prevOrder.id);
              const isCancelled   = cancelledOrderIds.has(prevOrder.id) || prevOrder.OrderStatus?.key === 'cancelled';
              const prevEntries   = groupOrderItems(prevOrder.orderItems || []);
              const itemsDisplay  = prevEntries.length > 0
                ? prevEntries.map((entry, idx) => {
                    const sep = idx < prevEntries.length - 1 && ', ';
                    if (entry.type === 'bundle' && entry.bundleName) {
                      return <span key={entry.groupKey} className={styles.prevOrderItem}>{entry.bundleName} × {entry.bundleQuantity}{sep}</span>;
                    }
                    const rows = entry.type === 'bundle' ? entry.items : [entry.item];
                    return rows.map((item, rIdx) => (
                      <span key={`${idx}-${rIdx}`} className={styles.prevOrderItem}>{item.Product?.name || 'Unknown'} – {parseFloat(item.quantity)}{(rIdx < rows.length - 1 ? ', ' : sep)}</span>
                    ));
                  })
                : <span className={styles.prevOrderItem}>—</span>;
              return (
                <div key={prevOrder.id} className={`${styles.previousOrderCard} ${isCancelled ? styles.previousOrderCardCancelled : ''}`}>
                  <div className={styles.previousOrderHeader}>
                    <div className={styles.previousOrderNumber}>#{prevOrder.orderNumber}</div>
                    <div className={styles.previousOrderStatus} style={{ backgroundColor: prevOrder.OrderStatus?.colorCode ? `${prevOrder.OrderStatus.colorCode}20` : '#F59E0B20', borderColor: prevOrder.OrderStatus?.colorCode || '#F59E0B', color: prevOrder.OrderStatus?.colorCode || '#92400e' }}>{prevOrder.OrderStatus?.label}</div>
                  </div>
                  <div className={styles.previousOrderDetails}>
                    <div className={styles.previousOrderInfo}><MapPin size={12} /><span>{prevOrder.address}, {prevOrder.City?.name}</span></div>
                    <div className={styles.previousOrderInfo}><Package size={12} /><span className={styles.prevOrderItemsList}>{itemsDisplay}</span></div>
                    <div className={styles.previousOrderInfo}><Calendar size={12} /><span>{formatDateTime(prevOrder.createdAt)}</span></div>
                  </div>
                  <div className={styles.previousOrderFooter}>
                    <div className={styles.previousOrderTotal}>{formatCurrency(prevOrder.grandTotal)}</div>
                    <div className={styles.previousOrderActions}>
                      <button onClick={() => window.open(`/orders/${prevOrder.id}`, '_blank')} className={styles.previousOrderButton}><Eye size={14} />{t('common.view')}<ExternalLink size={11} /></button>
                      <button onClick={() => window.open(`/orders/${prevOrder.id}/edit`, '_blank')} className={styles.previousOrderButton}><Edit size={14} />{t('common.edit')}<ExternalLink size={11} /></button>
                      {isCancellable && (
                        <button onClick={() => handleCancelAsDuplicate(prevOrder)} disabled={isCancelling} className={`${styles.previousOrderButton} ${styles.previousOrderCancelButton}`}>
                          <CancelIcon size={14} />{isCancelling ? 'Cancelling…' : 'Cancel Duplicate'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <h2>{t('orderAssignment.orderDetails')}</h2>
            <p className={styles.orderNumber}>#{localOrder.orderNumber}</p>
            <span className={styles.assignedToBadge} title={t('orderAssignment.assignedTo')}>
              <Users size={13} />
              {assignedUsers.length > 0 ? assignedUsers.map((u) => u.name).join(', ') : t('orderAssignment.notAssigned')}
            </span>
            {localOrder.confirmedByUser && (
              <span className={styles.assignedToBadge} title={t('ecomOrders.confirmedBy')}>
                <CheckCircle size={13} />
                {localOrder.confirmedByUser.name}{localOrder.confirmedAt && ` · ${formatDateTime(localOrder.confirmedAt)}`}
              </span>
            )}
            {localOrder.isUrgent && <span className={styles.urgentBadge}><AlertCircle size={14} />Urgent</span>}
          </div>
          <div className={styles.headerRight}>
            <button onClick={handleViewOrder} className={styles.viewOrderButton} title={t('common.view')}><Eye size={16} />{t('common.view')}<ExternalLink size={14} /></button>
            {canEdit && <button onClick={handleEditOrder} className={styles.editButton} title="Edit order"><Edit size={16} />Edit<ExternalLink size={14} /></button>}
            <button onClick={onClose} className={styles.closeButton}><X size={20} /></button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          <div className={styles.twoColumnGrid}>

            {/* LEFT */}
            <div className={styles.leftColumn}>

              {/* Customer Information */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}><User size={18} />{t('orderAssignment.customerInformation')}</h3>
                  {canEdit && !isDispatched && (
                    <button className={`${styles.urgentToggle} ${localOrder.isUrgent ? styles.active : ''}`} onClick={handleToggleUrgent} disabled={isUpdating}>
                      <AlertCircle size={16} />{localOrder.isUrgent ? 'Unmark Urgent' : 'Mark as Urgent'}
                    </button>
                  )}
                </div>
                <div className={styles.compactInfoGrid}>

                  {/* Customer Name */}
                  <div className={styles.infoItem}>
                    <div className={styles.infoItemHeader}>
                      <label>{t('orderAssignment.customerName')}</label>
                      {canEdit && !isDispatched && !isEditingCustomerName && (
                        <button
                          className={styles.editIconButton}
                          onClick={() => { setCustomerNameValue(localOrder.customerName || ''); setIsEditingCustomerName(true); }}
                          title="Edit customer name"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                    </div>
                    {isEditingCustomerName ? (
                      <div className={styles.editField}>
                        <input
                          type="text"
                          value={customerNameValue}
                          onChange={(e) => setCustomerNameValue(e.target.value)}
                          className={styles.textInput}
                          placeholder="Enter customer name"
                          disabled={isUpdating}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveCustomerName();
                            if (e.key === 'Escape') handleCancelCustomerName();
                          }}
                        />
                        <div className={styles.editActions}>
                          <button className={styles.saveButton} onClick={handleSaveCustomerName} disabled={isUpdating || !customerNameValue.trim()}>
                            <Save size={14} />{isUpdating ? 'Saving…' : 'Save'}
                          </button>
                          <button className={styles.cancelEditButton} onClick={handleCancelCustomerName} disabled={isUpdating}>
                            <CancelIcon size={14} />Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p>{localOrder.customerName}</p>
                    )}
                  </div>

                  {/* Phone Numbers */}
                  <div className={styles.infoItem}>
                    <div className={styles.infoItemHeader}>
                      <label>{t('orderAssignment.phone')}</label>
                      {canEdit && !isDispatched && !isEditingPhoneNumbers && (
                        <button
                          className={styles.editIconButton}
                          onClick={handleOpenPhoneEdit}
                          title="Edit phone numbers"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                    </div>
                    {renderPhoneNumbers()}
                  </div>

                  {/* Address */}
                  <div className={styles.infoItem}>
                    <div className={styles.infoItemHeader}>
                      <label>{t('orderAssignment.address')}</label>
                      {canEdit && !isDispatched && !isEditingAddress && (
                        <button className={styles.editIconButton} onClick={() => setIsEditingAddress(true)} title="Edit address and city"><Edit2 size={14} /></button>
                      )}
                    </div>
                    {isEditingAddress ? renderAddressEdit() : (
                      <p className={styles.addressText}><MapPin size={14} />{localOrder.address}, {localOrder.City?.name || 'N/A'}
                        {isCityUnknown(localOrder) && <span className={styles.cityUnknownBadge}>Incomplete Address</span>}
                      </p>
                    )}
                  </div>

                  {/* Delivery Notes */}
                  <div className={styles.infoItem}>
                    <div className={styles.infoItemHeader}>
                      <label>{t('ecomOrders.deliveryNotes')}</label>
                      {canEdit && !isDispatched && !isEditingDeliveryNotes && (
                        <button className={styles.editIconButton} onClick={() => setIsEditingDeliveryNotes(true)} title="Edit delivery notes"><Edit2 size={14} /></button>
                      )}
                    </div>
                    {isEditingDeliveryNotes ? (
                      <div className={styles.editField}>
                        <div className={styles.textareaWrapper}>
                          <input type="text" value={deliveryNotesValue} onChange={(e) => setDeliveryNotesValue(e.target.value)} className={styles.textInput} placeholder="Enter delivery notes..." maxLength={40} disabled={isUpdating} />
                          <span className={styles.charCount}>{deliveryNotesValue.length}/40</span>
                        </div>
                        <div className={styles.editActions}>
                          <button className={styles.saveButton} onClick={handleSaveDeliveryNotes} disabled={isUpdating}><Save size={14} />Save</button>
                          <button className={styles.cancelEditButton} onClick={handleCancelDeliveryNotes} disabled={isUpdating}><CancelIcon size={14} />Cancel</button>
                        </div>
                      </div>
                    ) : <p className={styles.notesText}>{localOrder.deliveryNotes || 'No delivery notes'}</p>}
                  </div>

                  {/* Special Notes */}
                  <div className={styles.infoItem}>
                    <div className={styles.infoItemHeader}>
                      <label>{t('ecomOrders.specialNotes')}</label>
                      {canEdit && !isDispatched && !isEditingSpecialNotes && (
                        <button className={styles.editIconButton} onClick={() => setIsEditingSpecialNotes(true)} title="Edit special notes"><Edit2 size={14} /></button>
                      )}
                    </div>
                    {isEditingSpecialNotes ? (
                      <div className={styles.editField}>
                        <textarea value={specialNotesValue} onChange={(e) => setSpecialNotesValue(e.target.value)} className={styles.textarea} rows={2} placeholder="Enter special notes..." disabled={isUpdating} />
                        <div className={styles.editActions}>
                          <button className={styles.saveButton} onClick={handleSaveSpecialNotes} disabled={isUpdating}><Save size={14} />Save</button>
                          <button className={styles.cancelEditButton} onClick={handleCancelSpecialNotes} disabled={isUpdating}><CancelIcon size={14} />Cancel</button>
                        </div>
                      </div>
                    ) : <p className={styles.notesText}>{localOrder.specialNotes || 'No special notes'}</p>}
                  </div>
                </div>
              </div>

              {renderPreviousOrders()}

              {/* Order Items */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}><Package size={18} />{t('orderAssignment.orderItems')}</h3>
                  {canEdit && !isDispatched && !isEditingItems && (
                    <button className={styles.editIconButton} onClick={openItemsEdit} title="Edit order items and delivery charge">
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>

                {isEditingItems ? renderItemsEditBlock() : renderItemsReadOnly()}
              </div>
            </div>

            {/* RIGHT */}
            <div className={styles.rightColumn}>
              {/* Order Summary */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}><DollarSign size={18} />{t('orderAssignment.orderSummary')}</h3>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryRow}><span>{t('orderAssignment.total')}</span><span>{formatCurrency(localOrder.totalAmount)}</span></div>
                  {localOrder.totalDiscount > 0 && (
                    <div className={styles.summaryRow}><span>{t('ecomOrders.totalDiscount')}</span><span className={styles.discountAmount}>-{formatCurrency(localOrder.totalDiscount)}</span></div>
                  )}
                  <div className={styles.summaryRow}><span>{t('ecomOrders.deliveryCharge')}</span><span>{formatCurrency(localOrder.deliveryCharge)}</span></div>
                  <div className={`${styles.summaryRow} ${styles.grandTotalRow}`}><span>{t('orderAssignment.grandTotal')}</span><span>{formatCurrency(localOrder.grandTotal)}</span></div>
                {localOrder.PaymentType?.label && (
                  <div className={`${styles.summaryRow} ${styles.paymentTypeRow}`}>
                    <span>{t('ecomOrders.paymentType')}</span>
                    <span>{localOrder.PaymentType.label}</span>
                  </div>
                )}
                {localOrder.paymentRecords?.length > 0 && (
                  <div className={styles.paymentRecordsList}>
                    {localOrder.paymentRecords.map((record, idx) => (
                      <div key={idx} className={styles.paymentRecordItem}>
                        <div className={styles.paymentRecordMeta}>
                          <PaymentMethodIcon
                            iconFileName={record.PaymentMethod?.iconFileName}
                            label={record.PaymentMethod?.label}
                            className={styles.paymentRecordMethod}
                            width={32}
                            verified={record.isVerified}
                          />
                          {record.reference && <span className={styles.paymentRecordRef}>{record.reference}</span>}
                        </div>
                        <span className={styles.paymentRecordAmount}>{formatCurrency(record.paymentAmount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                </div>
              </div>

              {/* Order Status */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}><Clock size={18} />{t('ecomOrders.orderStatus')}</h3>
                <div className={styles.compactInfoGrid}>
                  <div className={styles.infoItem}>
                    <label>{t('orderAssignment.status')}</label>
                    <div className={styles.statusBadge} style={{ backgroundColor: localOrder.OrderStatus?.colorCode ? `${localOrder.OrderStatus.colorCode}20` : '#F59E0B20', borderColor: localOrder.OrderStatus?.colorCode || '#F59E0B', color: localOrder.OrderStatus?.colorCode || '#92400e' }}>
                      {localOrder.OrderStatus?.label || localOrder.orderStatusKey}
                    </div>
                  </div>
                  {localOrder.OrderStatusReason && <div className={styles.infoItem}><label>{t('ecomOrders.statusReason')}</label><p>{localOrder.OrderStatusReason.label}</p></div>}
                  {localOrder.holdUntilDate && <div className={styles.infoItem}><label>{t('ecomOrders.holdUntilDate')}</label><p>{formatDate(localOrder.holdUntilDate)}</p></div>}
                  <div className={styles.infoItem}>
                    <label>{t('orderAssignment.callAttempts')}</label>
                    <div className={styles.callAttemptsDisplay}><PhoneIcon size={14} /><span>{localOrder.callAttempts || 0}</span></div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}><Store size={18} />Additional Info</h3>
                <div className={styles.compactInfoGrid}>
                  <div className={styles.infoItem}><label>{t('ecomOrders.shopName')}</label><p>{localOrder.Shop?.name || 'N/A'}</p></div>
                  {/* <div className={styles.infoItem}><label>{t('ecomOrders.paymentType')}</label><p>{localOrder.PaymentType?.label || localOrder.paymentTypeKey}</p></div> */}
                  {localOrder.airwaybillNumber && <div className={styles.infoItem}><label>{t('ecomOrders.airwaybillNumber')}</label><p className={styles.airwaybillText}>{localOrder.airwaybillNumber}</p></div>}
                </div>
              </div>
            </div>
          </div>

          {error && <div className={styles.errorMessage}><AlertCircle size={16} />{error}</div>}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter} ref={footerRef}>
          {renderStatusButtons()}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;